import {
  createReference,
  getAllQuestionnaireAnswers,
  getDisplayString,
  getExtensionValue,
  getReferenceString,
  LOINC,
  MedplumClient,
  parseReference,
  resolveId,
} from "@medplum/core";
import {
  Address,
  Bundle,
  BundleEntry,
  CodeableConcept,
  Coding,
  ContactPoint,
  Coverage,
  HumanName,
  Identifier,
  InsurancePlan,
  Organization,
  Patient,
  QuestionnaireResponse,
  Reference,
  RelatedPerson,
} from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import {
  getPracticeOrganizationReference,
  resolveInsuranceCompanyReference,
} from "@/billing/utils/custom-insurance-organization";
import { searchTierCoverages, selectTierCoverage } from "@/bots/utils/coverage-tier";
import { SentryInstance } from "@/bots/utils/sentry";
import { Extensions } from "@/fhir/enums/extensions";
import { NO_DISPLAY } from "@/intake-forms/constants/yes-no-display";
import { readYesNoDisplayFromAnswer } from "@/intake-forms/utils/yes-no-questionnaire-answer";
import { joinReferenceString, setExtensionValue } from "@/utils/medplum";
import { pruneDisabledAnswers } from "@/utils/questionnaire-response";

import {
  omitDisabledQuestionnaireAnswers,
  resolveQuestionnaireForResponse,
} from "../enable-when-gate";
import {
  cancelOnClearObservation,
  logObservationCancelOnClearFailure,
} from "../observation-cancel-on-clear";
import {
  Answers,
  convertYesNoToBoolean,
  get,
  logBundleEntryError,
  ObservationValues,
  Systems,
} from "../utils";
import {
  INSURANCE_TIER_ORDER,
  INSURANCE_TIERS,
  InsuranceTierConfig,
  InsuranceTierName,
} from "../utils/billing-insurance-tiers";
import {
  applyLateVerificationsToCreatedCoverages,
  fetchActiveVerificationResponses,
  findPayorVerificationAnswers,
} from "../utils/payor-verification";

import { buildCoverage, CoverageStatus, isSamePayor } from "./billing-coverage";
import { AnswersOptions } from "./billing-information-answers";

export { AnswersOptions };

enum QuiltedHealthCodes {
  MedicaidEligibility = "forms/intake/medicaid-eligibilty",
  BillingAndInsuranceComments = "forms/intake/billing-and-insurance-comments",
}

enum LOINCCodes {
  WICAssistanceParticipant = "89088-9",
}

enum LOINCAnswerCodes {
  Yes = "LA33-6",
  No = "LA32-8",
  Unknown = "LA4489-6",
}

enum NameUse {
  Official = "official",
}

enum YesNoOrDontKnow {
  Yes = "Yes",
  No = "No",
  Unknown = "Don't know",
}

function createHumanName(family: string, given: string[], use: NameUse): HumanName {
  return {
    family,
    given,
    use,
  };
}

function getInsuranceAddressFromAnswers(
  answers: Answers,
  addressOptions: {
    address: AnswersOptions;
    city: AnswersOptions;
    state: AnswersOptions;
    zipCode: AnswersOptions;
    country: AnswersOptions;
    addressDetails: AnswersOptions;
  },
): Address | undefined {
  const streetAddress = get(answers[addressOptions.address]) as string;
  const city = get(answers[addressOptions.city]) as string;
  const state = get(answers[addressOptions.state]) as Coding;
  const zipCode = get(answers[addressOptions.zipCode]) as string;
  const country = get(answers[addressOptions.country]) as Coding;
  const addressDetails = get(answers[addressOptions.addressDetails]) as string;

  const hasAnyAddressField =
    (streetAddress && streetAddress.trim()) ||
    (city && city.trim()) ||
    (state?.code && state.code.trim()) ||
    (zipCode && zipCode.trim()) ||
    (country?.code && country.code.trim()) ||
    (addressDetails && addressDetails.trim());

  if (!hasAnyAddressField) return undefined;

  const line = [streetAddress, addressDetails].filter((a) => a && a.trim()) as string[];
  const text = line.length > 0 ? line.join(", ") : undefined;

  return {
    ...(text && { text }),
    ...(line.length > 0 && { line }),
    ...(city && { city }),
    ...(state?.code && { state: state.code }),
    ...(zipCode && { postalCode: zipCode }),
    ...(country?.code && { country: country.code }),
  };
}

const MEDICAID_ELIGIBILITY_CODE: CodeableConcept = {
  coding: [
    {
      system: Systems.QH,
      code: QuiltedHealthCodes.MedicaidEligibility,
      display: "Qualifies for the state Medicaid program",
    },
  ],
};

const BILLING_AND_INSURANCE_COMMENTS_CODE: CodeableConcept = {
  coding: [
    {
      system: Systems.QH,
      code: QuiltedHealthCodes.BillingAndInsuranceComments,
      display: "Insurance and Billing Comments",
    },
  ],
};

const WIC_ASSISTANCE_PARTICIPANT_CODE: CodeableConcept = {
  coding: [
    {
      system: LOINC,
      code: LOINCCodes.WICAssistanceParticipant,
      display: "WIC Status",
    },
  ],
};

function getClientMedicaidObservationValue(answers: Answers): ObservationValues | undefined {
  const medicaidEligibilityAnswer = get(
    answers[AnswersOptions.BillingInformationStateMedicaidProgram],
  ) as YesNoOrDontKnow;

  if (!medicaidEligibilityAnswer) return undefined;

  let loincAnswerCode = LOINCAnswerCodes.Unknown;

  if (medicaidEligibilityAnswer === YesNoOrDontKnow.Yes) loincAnswerCode = LOINCAnswerCodes.Yes;
  else if (medicaidEligibilityAnswer === YesNoOrDontKnow.No) loincAnswerCode = LOINCAnswerCodes.No;

  return {
    valueCodeableConcept: {
      coding: [{ system: LOINC, code: loincAnswerCode, display: medicaidEligibilityAnswer }],
    },
  };
}

function getInsuranceAndBillingCommentsObservationValue(
  answers: Answers,
): ObservationValues | undefined {
  const billingAndInsuranceCommentsAnswer = get(
    answers[AnswersOptions.BillingInformationInsuranceAndBillingComments],
  ) as string;

  if (!billingAndInsuranceCommentsAnswer) return undefined;

  return { valueString: billingAndInsuranceCommentsAnswer };
}

function getClientWICObservationValue(answers: Answers): ObservationValues | undefined {
  const isReceivingWICAssistance = get(
    answers[AnswersOptions.BillingInformationReceiveWICAssistance],
  ) as YesNoOrDontKnow;

  if (!isReceivingWICAssistance) return undefined;

  return {
    valueCodeableConcept: {
      coding: [
        convertYesNoToBoolean(isReceivingWICAssistance)
          ? { system: LOINC, code: LOINCAnswerCodes.Yes, display: "Yes" }
          : { system: LOINC, code: LOINCAnswerCodes.No, display: "No" },
      ],
    },
  };
}

/**
 * Saves billing's three single-active Observation answers (Medicaid, WIC, insurance comments)
 * separately from the Coverage/RelatedPerson batch so they still save if that batch fails.
 */
async function reconcileBillingObservations(
  medplum: MedplumClient,
  patient: Patient,
  answers: Answers,
  sentry: SentryInstance,
): Promise<void> {
  const results = await Promise.all([
    cancelOnClearObservation(
      medplum,
      patient,
      MEDICAID_ELIGIBILITY_CODE,
      getClientMedicaidObservationValue(answers),
    ),
    cancelOnClearObservation(
      medplum,
      patient,
      WIC_ASSISTANCE_PARTICIPANT_CODE,
      getClientWICObservationValue(answers),
    ),
    cancelOnClearObservation(
      medplum,
      patient,
      BILLING_AND_INSURANCE_COMMENTS_CODE,
      getInsuranceAndBillingCommentsObservationValue(answers),
    ),
  ]);

  results.forEach((result) => {
    if (!result.ok) {
      logObservationCancelOnClearFailure(result, "Billing Information", sentry);
    }
  });
}

/**
 * Returns true when `buildSubscriberRelatedPerson` should create or update a RelatedPerson for
 * this tier: relationship is not Self, and a first or last name was provided.
 */
function tierNeedsSubscriberRecord(tier: InsuranceTierConfig, answers: Answers): boolean {
  if (tier.isOwner(answers)) return false;

  const firstName = get(answers[tier.subscriberFields.firstName]) as string | undefined;
  const lastName = get(answers[tier.subscriberFields.lastName]) as string | undefined;
  return Boolean(firstName?.trim()) || Boolean(lastName?.trim());
}

/**
 * True when this tier's "do you have insurance?" answer is "No" (valueString or Coding).
 * Used when Questionnaire enableWhen omit did not run (missing Questionnaire on the
 * QuestionnaireResponse), so leftover company/subscriber answers still trigger
 * Coverage/RelatedPerson cleanup.
 */
function isInsuranceTierOff(tier: InsuranceTierConfig, answers: Answers): boolean {
  return readYesNoDisplayFromAnswer(answers[tier.hasInsuranceAnswer]?.[0]) === NO_DISPLAY;
}

/**
 * Returns the RelatedPerson id from `coverage.subscriber`, or undefined when there is no Coverage,
 * the subscriber is the client (`Patient`), or the reference is missing/malformed.
 *
 * Uses `resolveId` so malformed subscriber references return undefined and billing falls back to
 * SSN search. Assumes relative `Type/id` strings (what `createReference` / `joinReferenceString`
 * write); absolute FHIR URLs are treated as unreadable and also fall back to SSN.
 */
function getReferencedRelatedPersonId(coverage: Coverage | undefined): string | undefined {
  const subscriberReference = coverage?.subscriber;
  const reference = subscriberReference?.reference;
  if (!reference) return undefined;

  // Client-as-self subscribers are Patient refs — expected, not RelatedPerson.
  if (reference.startsWith("Patient/")) return undefined;

  if (!reference.startsWith("RelatedPerson/")) {
    console.warn(
      `[billing-information] Ignoring non-RelatedPerson Coverage.subscriber "${reference}" on Coverage/${coverage?.id ?? "unknown"}`,
    );
    return undefined;
  }

  const id = resolveId(subscriberReference);
  return id || undefined;
}

/**
 * Returns true when another active or cancelled Coverage will still list `relatedPersonId` as
 * subscriber after this save. Ids in `coverageIdsNoLongerReferencingSubscriber` are switching to
 * Self or being deleted in this batch, so they do not count as keepers.
 */
async function isRelatedPersonReferencedByOtherCoverage(
  medplum: MedplumClient,
  relatedPersonId: string,
  excludingCoverageId: string | undefined,
  coverageIdsNoLongerReferencingSubscriber: ReadonlySet<string>,
): Promise<boolean> {
  const referencingCoverages = await medplum.searchResources("Coverage", {
    subscriber: joinReferenceString("RelatedPerson", relatedPersonId),
    status: "active,cancelled",
  });
  return referencingCoverages.some(
    (coverage) =>
      !!coverage.id &&
      coverage.id !== excludingCoverageId &&
      !coverageIdsNoLongerReferencingSubscriber.has(coverage.id),
  );
}

/**
 * Returns at most one DELETE entry per RelatedPerson id.
 * Used when several tiers release the same shared subscriber in one save.
 */
function uniqueRelatedPersonDeleteEntries(
  entries: Array<BundleEntry<RelatedPerson> | undefined>,
): BundleEntry<RelatedPerson>[] {
  const seenIds = new Set<string>();
  return entries.reduce<BundleEntry<RelatedPerson>[]>((unique, entry) => {
    const id = entry?.resource?.id;
    if (!entry || !id || seenIds.has(id)) return unique;
    seenIds.add(id);
    unique.push(entry);
    return unique;
  }, []);
}

type ResolvedSubscriberRelatedPerson = {
  existingRelatedPerson?: RelatedPerson;
  deleteEntry?: BundleEntry<RelatedPerson>;
};

/**
 * Removes RelatedPerson DELETE entries when another tier in this save still resolved that
 * RelatedPerson (`existingRelatedPerson`). Needed when a sibling tier is newly added and adopts
 * the subscriber by SSN before any Coverage for that tier exists in the database.
 */
function suppressRelatedPersonDeletesRetainedInBatch(
  resolutions: ResolvedSubscriberRelatedPerson[],
): ResolvedSubscriberRelatedPerson[] {
  const retainedRelatedPersonIds = new Set(
    resolutions.reduce<string[]>((ids, resolution) => {
      if (resolution.existingRelatedPerson?.id) ids.push(resolution.existingRelatedPerson.id);
      return ids;
    }, []),
  );

  return resolutions.map((resolution) => {
    const deleteId = resolution.deleteEntry?.resource?.id;
    if (deleteId && retainedRelatedPersonIds.has(deleteId)) {
      return { existingRelatedPerson: resolution.existingRelatedPerson };
    }
    return resolution;
  });
}

/**
 * Finds the subscriber RelatedPerson to reuse for one insurance tier.
 *
 * Returns `deleteEntry` when the client is Self, the tier's Coverage will be deleted, or
 * `forceCleanup` is set (insurance "No"), unless another Coverage still
 * needs that person after this save. Sibling Coverages in
 * `coverageIdsNoLongerReferencingSubscriber` do not count as keepers.
 * `suppressRelatedPersonDeletesRetainedInBatch` also keeps a RelatedPerson that a sibling tier
 * adopts in this same save (for example by SSN).
 *
 * When relationship stays non-self, Coverage remains, and cleanup is not forced, returns
 * `existingRelatedPerson` even if name fields are blank, so Coverage keeps a stable subscriber
 * reference. `buildSubscriberRelatedPerson` may skip a PUT when names are blank.
 *
 * Lookup: `Coverage.subscriber` RelatedPerson first; then SSN search when that read fails or there
 * is no RelatedPerson reference yet. An SSN change on a resolved RelatedPerson updates that same
 * record when a PUT is written.
 *
 * @returns `existingRelatedPerson` to reuse or update, and/or `deleteEntry` to remove the prior
 * subscriber RelatedPerson.
 */
async function resolveSubscriberRelatedPerson(
  medplum: MedplumClient,
  clientId: string,
  tier: InsuranceTierConfig,
  answers: Answers,
  existingCoverage: Coverage | undefined,
  sentry: SentryInstance,
  coverageIdsNoLongerReferencingSubscriber: ReadonlySet<string>,
  willDeleteCoverage: boolean,
  forceCleanup: boolean,
): Promise<{ existingRelatedPerson?: RelatedPerson; deleteEntry?: BundleEntry<RelatedPerson> }> {
  const referencedRelatedPersonId = getReferencedRelatedPersonId(existingCoverage);
  const subscriberSSN = get(answers[tier.subscriberFields.subscriberId]) as string | undefined;

  let existingRelatedPerson: RelatedPerson | undefined;
  if (referencedRelatedPersonId) {
    existingRelatedPerson = await medplum
      .readResource("RelatedPerson", referencedRelatedPersonId)
      .catch((error) => {
        if (sentry) {
          sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
            extra: { relatedPersonId: referencedRelatedPersonId, coverageId: existingCoverage?.id },
          });
        }
        return undefined;
      });
  }

  // Search by SSN when Coverage.subscriber has no readable RelatedPerson.
  if (!existingRelatedPerson && !tier.isOwner(answers) && subscriberSSN?.trim()) {
    const matches = await medplum.searchResources("RelatedPerson", {
      patient: joinReferenceString("Patient", clientId),
      identifier: `${Systems.SocialSecurity}|${subscriberSSN}`,
    });
    existingRelatedPerson = matches[0];
  }

  const shouldDeleteRelatedPerson =
    Boolean(existingRelatedPerson?.id) &&
    (forceCleanup || tier.isOwner(answers) || willDeleteCoverage);

  // Self, cleared tier, or insurance "No": DELETE RelatedPerson when no other tier needs it.
  if (shouldDeleteRelatedPerson && existingRelatedPerson?.id) {
    const referencedByOtherTier = await isRelatedPersonReferencedByOtherCoverage(
      medplum,
      existingRelatedPerson.id,
      existingCoverage?.id,
      coverageIdsNoLongerReferencingSubscriber,
    );
    if (referencedByOtherTier) return {};

    return {
      deleteEntry: {
        resource: existingRelatedPerson,
        request: { method: "DELETE", url: getReferenceString(existingRelatedPerson)! },
      },
    };
  }

  if (forceCleanup) return {};

  return { existingRelatedPerson };
}

/**
 * Loads the Questionnaire on the response (when present) and clears answers on disabled enableWhen
 * items before billing field resolution. Continues with the raw response when the Questionnaire
 * cannot be loaded.
 */
async function pruneBillingQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse,
  sentry: SentryInstance,
): Promise<QuestionnaireResponse> {
  const questionnaireCanonical = questionnaireResponse.questionnaire;
  if (!questionnaireCanonical) return questionnaireResponse;

  try {
    const [, questionnaireId] = parseReference({ reference: questionnaireCanonical });
    const questionnaire = await medplum.readResource("Questionnaire", questionnaireId);
    return pruneDisabledAnswers(questionnaire, questionnaireResponse);
  } catch (error) {
    if (sentry) {
      sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: { questionnaire: questionnaireCanonical },
      });
    }
    return questionnaireResponse;
  }
}

/** True when `buildCoverage` will DELETE this tier's existing Coverage (no payor). */
function willDeleteTierCoverage(
  organization: Reference<Organization> | undefined,
  existingCoverage: Coverage | undefined,
): boolean {
  return !organization && Boolean(existingCoverage);
}

/**
 * Builds or updates the subscriber RelatedPerson for one insurance tier from intake answers.
 * @param tier Field mapping and owner check for the tier being processed (primary/secondary/tertiary).
 * @param answers All questionnaire answers for the billing section.
 * @param client Patient the RelatedPerson is linked to.
 * @param billingRelatedPersonFullUrl Bundle fullUrl (or resolved `RelatedPerson/id` reference) for
 * this tier's subscriber.
 * @param hasPayor True when this tier resolved a payor and will keep a Coverage. A subscriber
 * without a Coverage would be orphaned, so none is written. False also for a tier answered "No",
 * since that tier skips payor resolution.
 * @param existingRelatedPerson Existing RelatedPerson to PUT/update, if one was already resolved
 * for this tier.
 * @returns A bundle entry for the subscriber RelatedPerson, or undefined if the tier has no payor,
 * the client is their own subscriber, or no subscriber name was provided.
 */
function buildSubscriberRelatedPerson(
  tier: InsuranceTierConfig,
  answers: Answers,
  client: Patient,
  billingRelatedPersonFullUrl: string,
  hasPayor: boolean,
  existingRelatedPerson?: RelatedPerson,
): BundleEntry<RelatedPerson> | undefined {
  if (!hasPayor || !tierNeedsSubscriberRecord(tier, answers)) return;

  const fields = tier.subscriberFields;
  const subscriberFirstName = get(answers[fields.firstName]) as string;
  const subscriberLastName = get(answers[fields.lastName]) as string;
  const subscriberMiddleName = get(answers[fields.middleName]) as string;
  const subscriberStreetAddress = get(answers[fields.streetAddress]) as string;
  const subscriberCity = get(answers[fields.city]) as string;
  const subscriberState = get(answers[fields.state]) as Coding;
  const subscriberZipCode = get(answers[fields.zipCode]) as string;
  const subscriberDayOfBirth = get(answers[fields.dateOfBirth]) as string;
  const subscriberID = get(answers[fields.subscriberId]) as string;
  const subscriberPhone = get(answers[fields.phone]);
  const subscriberType = get(answers[fields.phoneType]) as Coding;

  const subscriberGivenName: string[] = [];

  if (subscriberFirstName) subscriberGivenName.push(subscriberFirstName);
  if (subscriberMiddleName) subscriberGivenName.push(subscriberMiddleName);

  const familyName =
    subscriberLastName && subscriberLastName.trim().length > 0 ? subscriberLastName : "";

  const subscriberHumanName = createHumanName(familyName, subscriberGivenName, NameUse.Official);

  if (
    (!subscriberHumanName.family || subscriberHumanName.family.trim().length === 0) &&
    (!subscriberHumanName.given ||
      subscriberHumanName.given.length === 0 ||
      !subscriberHumanName.given.some((g) => g && g.trim().length > 0))
  ) {
    return;
  }

  const subscriberTelecom: ContactPoint = {
    value: (subscriberPhone as string) ?? "",
    use: subscriberType?.code as ContactPoint["use"],
  };

  const subscriberAddressArray: Address[] | undefined = subscriberStreetAddress
    ? [
        {
          text: subscriberStreetAddress ?? "",
          line: [subscriberStreetAddress].filter((address) => !!address?.trim()) as string[],
          postalCode: subscriberZipCode ?? "",
          city: subscriberCity ?? "",
          state: subscriberState?.code ?? "",
        },
      ]
    : undefined;

  const subscriberIdentifier: Identifier[] = [];

  if (subscriberID) {
    subscriberIdentifier.push({
      type: {
        coding: [
          {
            system: Systems.IdentifierType,
            code: "SS",
          },
        ],
      },
      system: Systems.SocialSecurity,
      value: subscriberID,
    });
  }

  const relatedPerson: RelatedPerson = {
    ...(existingRelatedPerson ?? {}),
    resourceType: "RelatedPerson",
    id: existingRelatedPerson?.id,
    patient: createReference(client),
    name: [subscriberHumanName],
    telecom: [subscriberTelecom],
    address: subscriberAddressArray,
    birthDate: subscriberDayOfBirth,
    identifier: subscriberIdentifier,
  };

  return {
    fullUrl: billingRelatedPersonFullUrl,
    resource: relatedPerson,
    request: {
      method: existingRelatedPerson ? "PUT" : "POST",
      url: existingRelatedPerson ? getReferenceString(existingRelatedPerson)! : "RelatedPerson",
    },
  };
}

/**
 * Builds the Coverage.subscriber reference for one tier, with `display` set when possible so
 * admin and other views show a name. Uses `createReference` when the target resource has an id.
 * For a new RelatedPerson known only by bundle fullUrl, builds `{ reference, display }` by hand —
 * `createReference` would produce `RelatedPerson/undefined` before the resource has an id.
 * @param tier Field mapping and owner check for the tier being processed.
 * @param answers All questionnaire answers for the billing section.
 * @param client The client Patient, used when they are their own subscriber.
 * @param billingRelatedPersonFullUrl Bundle fullUrl for this tier's subscriber RelatedPerson, used
 * when it has no `id` yet (being created in this same batch).
 * @param relatedPersonResource The subscriber RelatedPerson resource being written this batch
 * (new or reused), if the client is not their own subscriber and one was resolved.
 * @returns A reference to the client or the subscriber RelatedPerson, with `display` set whenever
 * derivable.
 */
function resolveSubscriberReference(
  tier: InsuranceTierConfig,
  answers: Answers,
  client: Patient,
  billingRelatedPersonFullUrl: string,
  relatedPersonResource?: RelatedPerson,
): Reference<Patient | RelatedPerson> {
  if (tier.isOwner(answers)) return createReference(client);
  if (!relatedPersonResource) return { reference: billingRelatedPersonFullUrl };
  if (relatedPersonResource.id) return createReference(relatedPersonResource);

  return {
    reference: billingRelatedPersonFullUrl,
    display: getDisplayString(relatedPersonResource),
  };
}

type InsurancePlanTier = "primary" | "secondary" | "tertiary";

type InsurancePlanContactInput = {
  address?: Address;
  telecom?: ContactPoint[];
};

/**
 * Builds InsurancePlan.contact (address + phone) from intake answers for one tier.
 * See https://www.medplum.com/docs/api/fhir/resources/insuranceplan
 */
function getInsurancePlanContactFromAnswers(
  answers: Answers,
  addressOptions: {
    address: AnswersOptions;
    city: AnswersOptions;
    state: AnswersOptions;
    zipCode: AnswersOptions;
    country: AnswersOptions;
    addressDetails: AnswersOptions;
  },
  phoneOption: AnswersOptions,
  phoneTypeOption: AnswersOptions,
): InsurancePlanContactInput | undefined {
  const address = getInsuranceAddressFromAnswers(answers, addressOptions);
  const phone = get(answers[phoneOption]) as string | undefined;
  const phoneType = get(answers[phoneTypeOption]) as Coding | undefined;
  const hasPhone = phone && String(phone).trim();
  const telecom: ContactPoint[] = [];
  if (hasPhone) {
    telecom.push({
      system: "phone",
      value: String(phone).trim(),
      ...(phoneType?.code && { use: phoneType.code as ContactPoint["use"] }),
    });
  }
  if (!address && telecom.length === 0) return undefined;
  return {
    ...(address && { address }),
    ...(telecom.length > 0 && { telecom }),
  };
}

/**
 * Finds an existing InsurancePlan for the organization, name, and tier, or creates one.
 * Uses extension InsurancePlanTier so there is at most one plan per level (primary, secondary, tertiary)
 * per org+name. When contactInfo is provided, adds insurance address and phone to the plan (on create
 * or update so the plan stays in sync with intake).
 */
const INSURANCE_PLAN_SEARCH_PAGE_SIZE = 100;

async function getOrCreateInsurancePlan(
  medplum: MedplumClient,
  organizationRef: Reference<Organization>,
  planName: string,
  tier: InsurancePlanTier,
  contactInfo?: InsurancePlanContactInput,
): Promise<Reference<InsurancePlan>> {
  const ownedByRef = getReferenceString(organizationRef);
  let offset = 0;
  let existing: InsurancePlan | undefined;
  for (;;) {
    const page = await medplum.searchResources("InsurancePlan", {
      "owned-by": ownedByRef,
      name: planName,
      _count: String(INSURANCE_PLAN_SEARCH_PAGE_SIZE),
      _offset: String(offset),
    });
    existing = page.find(
      (p) => (getExtensionValue(p, Extensions.InsurancePlanTier) as string) === tier,
    );
    if (existing) break;
    if (page.length < INSURANCE_PLAN_SEARCH_PAGE_SIZE) break;
    offset += INSURANCE_PLAN_SEARCH_PAGE_SIZE;
  }

  const contact =
    contactInfo && (contactInfo.address || (contactInfo.telecom?.length ?? 0) > 0)
      ? [
          {
            ...(contactInfo.address && { address: contactInfo.address }),
            ...(contactInfo.telecom &&
              contactInfo.telecom.length > 0 && { telecom: contactInfo.telecom }),
          },
        ]
      : undefined;

  if (existing) {
    if (contact) {
      await medplum.updateResource<InsurancePlan>({
        ...existing,
        contact,
      });
    }
    return createReference(existing);
  }

  const plan = await medplum.createResource<InsurancePlan>({
    resourceType: "InsurancePlan",
    status: "active",
    name: planName,
    ownedBy: organizationRef,
    extension: setExtensionValue([], Extensions.InsurancePlanTier, { valueString: tier }),
    ...(contact && { contact }),
  });
  return createReference(plan);
}

/**
 * Processes billing information section: reads patient, processes fields,
 * creates bundle entries, and executes batch to create/update resources.
 */
export async function processBillingInformation(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse,
  sentry: SentryInstance,
): Promise<boolean> {
  const prunedQuestionnaireResponse = await pruneBillingQuestionnaireResponse(
    medplum,
    questionnaireResponse,
    sentry,
  );
  const [_, clientId] = parseReference(prunedQuestionnaireResponse.subject);

  const profile = await medplum.readResource("Patient", clientId);

  if (!profile) throw new Error("Client does not exist");

  const practiceReference = getPracticeOrganizationReference(prunedQuestionnaireResponse, profile);
  if (!practiceReference?.reference)
    throw new Error(
      `No practice Organization for QuestionnaireResponse ${prunedQuestionnaireResponse.id}`,
    );

  // Read answers from the pruned response. When the Questionnaire is available, also omit any
  // remaining disabled answers (older responses or race conditions).
  const rawAnswers = getAllQuestionnaireAnswers(prunedQuestionnaireResponse);
  const questionnaire = await resolveQuestionnaireForResponse(medplum, prunedQuestionnaireResponse);
  const answers = questionnaire
    ? omitDisabledQuestionnaireAnswers(questionnaire, rawAnswers)
    : rawAnswers;

  // Start Observation writes now; await at the end so they run in parallel with the
  // Coverage/RelatedPerson batch built below.
  const observationReconciliationPromise = reconcileBillingObservations(
    medplum,
    profile,
    answers,
    sentry,
  );

  const billingRelatedPersonFullUrl = `urn:uuid:${uuidv4()}`;
  const secondBillingRelatedPersonFullUrl = `urn:uuid:${uuidv4()}`;
  const thirdBillingRelatedPersonFullUrl = `urn:uuid:${uuidv4()}`;

  const existingCoverages = await searchTierCoverages(
    medplum,
    clientId,
    practiceReference.reference,
  );

  const existingPrimaryCoverage = selectTierCoverage(existingCoverages, 1).target;
  const existingSecondaryCoverage = selectTierCoverage(existingCoverages, 2).target;
  const existingTertiaryCoverage = selectTierCoverage(existingCoverages, 3).target;

  const primaryContact = getInsurancePlanContactFromAnswers(
    answers,
    {
      address: AnswersOptions.BillingInformationAddress,
      city: AnswersOptions.BillingInformationCity,
      state: AnswersOptions.BillingInformationState,
      zipCode: AnswersOptions.BillingInformationZipCode,
      country: AnswersOptions.BillingInformationCountry,
      addressDetails: AnswersOptions.BillingInformationAddressDetails,
    },
    AnswersOptions.BillingInformationPhone,
    AnswersOptions.BillingInformationType,
  );

  const secondContact = getInsurancePlanContactFromAnswers(
    answers,
    {
      address: AnswersOptions.BillingInformationSecondInsuranceAddress,
      city: AnswersOptions.BillingInformationSecondInsuranceCity,
      state: AnswersOptions.BillingInformationSecondInsuranceState,
      zipCode: AnswersOptions.BillingInformationSecondInsuranceZipCode,
      country: AnswersOptions.BillingInformationSecondInsuranceCountry,
      addressDetails: AnswersOptions.BillingInformationSecondInsuranceAddressDetails,
    },
    AnswersOptions.BillingInformationSecondInsurancePhone,
    AnswersOptions.BillingInformationSecondInsuranceType,
  );

  const thirdContact = getInsurancePlanContactFromAnswers(
    answers,
    {
      address: AnswersOptions.BillingInformationThirdInsuranceAddress,
      city: AnswersOptions.BillingInformationThirdInsuranceCity,
      state: AnswersOptions.BillingInformationThirdInsuranceState,
      zipCode: AnswersOptions.BillingInformationThirdInsuranceZipCode,
      country: AnswersOptions.BillingInformationThirdInsuranceCountry,
      addressDetails: AnswersOptions.BillingInformationThirdInsuranceAddressDetails,
    },
    AnswersOptions.BillingInformationThirdInsurancePhone,
    AnswersOptions.BillingInformationThirdInsuranceType,
  );

  const existingCoverageByTier: Record<InsuranceTierName, Coverage | undefined> = {
    primary: existingPrimaryCoverage,
    secondary: existingSecondaryCoverage,
    tertiary: existingTertiaryCoverage,
  };

  const forceCleanupByTier = INSURANCE_TIER_ORDER.reduce(
    (result, name) => {
      result[name] = isInsuranceTierOff(INSURANCE_TIERS[name], answers);
      return result;
    },
    {} as Record<InsuranceTierName, boolean>,
  );

  // When a tier is forced off, skip payor resolution so leftover company answers cannot recreate
  // Coverage (handles the case where Questionnaire omit did not run).
  const firstHealthInsurance = forceCleanupByTier.primary
    ? undefined
    : await resolveInsuranceCompanyReference(
        medplum,
        get(answers[INSURANCE_TIERS.primary.companyAnswer]) as
          | Reference<Organization>
          | string
          | undefined,
        practiceReference,
        primaryContact,
        existingPrimaryCoverage?.payor?.[0] as Reference<Organization> | undefined,
      );

  const secondHealthInsurance = forceCleanupByTier.secondary
    ? undefined
    : await resolveInsuranceCompanyReference(
        medplum,
        get(answers[INSURANCE_TIERS.secondary.companyAnswer]) as
          | Reference<Organization>
          | string
          | undefined,
        practiceReference,
        secondContact,
        existingSecondaryCoverage?.payor?.[0] as Reference<Organization> | undefined,
      );

  const thirdHealthInsurance = forceCleanupByTier.tertiary
    ? undefined
    : await resolveInsuranceCompanyReference(
        medplum,
        get(answers[INSURANCE_TIERS.tertiary.companyAnswer]) as
          | Reference<Organization>
          | string
          | undefined,
        practiceReference,
        thirdContact,
        existingTertiaryCoverage?.payor?.[0] as Reference<Organization> | undefined,
      );

  if (firstHealthInsurance) {
    const primaryGroup = (get(answers[AnswersOptions.BillingInformationInsuranceGroupNumber]) ??
      "") as string;
    const primaryPlanName =
      primaryGroup && String(primaryGroup).trim()
        ? `Group ${String(primaryGroup).trim()}`
        : "Health Plan";
    await getOrCreateInsurancePlan(
      medplum,
      firstHealthInsurance as Reference<Organization>,
      primaryPlanName,
      "primary",
      primaryContact,
    );
  }
  if (secondHealthInsurance) {
    const secondGroup = (get(
      answers[AnswersOptions.BillingInformationSecondInsuranceGroupNumber],
    ) ?? "") as string;
    const secondPlanName =
      secondGroup && String(secondGroup).trim()
        ? `Group ${String(secondGroup).trim()}`
        : "Health Plan";
    await getOrCreateInsurancePlan(
      medplum,
      secondHealthInsurance as Reference<Organization>,
      secondPlanName,
      "secondary",
      secondContact,
    );
  }
  if (thirdHealthInsurance) {
    const thirdGroup = (get(answers[AnswersOptions.BillingInformationThirdInsuranceGroupNumber]) ??
      "") as string;
    const thirdPlanName =
      thirdGroup && String(thirdGroup).trim()
        ? `Group ${String(thirdGroup).trim()}`
        : "Health Plan";
    await getOrCreateInsurancePlan(
      medplum,
      thirdHealthInsurance as Reference<Organization>,
      thirdPlanName,
      "tertiary",
      thirdContact,
    );
  }

  const organizationByTier: Record<InsuranceTierName, Reference<Organization> | undefined> = {
    primary: firstHealthInsurance as Reference<Organization> | undefined,
    secondary: secondHealthInsurance as Reference<Organization> | undefined,
    tertiary: thirdHealthInsurance as Reference<Organization> | undefined,
  };

  const relatedPersonFullUrlByTier: Record<InsuranceTierName, string> = {
    primary: billingRelatedPersonFullUrl,
    secondary: secondBillingRelatedPersonFullUrl,
    tertiary: thirdBillingRelatedPersonFullUrl,
  };

  const tierStates = INSURANCE_TIER_ORDER.map((name) => {
    const tier = INSURANCE_TIERS[name];
    const organization = organizationByTier[name];
    // When a tier loses its payor, only its active Coverage is deleted. A cancelled Coverage
    // stays, and so does its subscriber RelatedPerson.
    const isTierRemoved = !organization;
    const existingCoverage = isTierRemoved
      ? selectTierCoverage(existingCoverages, tier.order, [CoverageStatus.Active]).target
      : existingCoverageByTier[name];
    return {
      name,
      tier,
      existingCoverage,
      organization,
      forceCleanup: forceCleanupByTier[name],
      willDeleteCoverage: willDeleteTierCoverage(organization, existingCoverage),
    };
  });

  // Coverages that switch to Self, are deleted, or get force cleanup this save. Excluded when
  // checking whether a shared RelatedPerson is still needed.
  const coverageIdsNoLongerReferencingSubscriber = new Set(
    tierStates.reduce<string[]>((ids, state) => {
      if (
        (state.tier.isOwner(answers) || state.willDeleteCoverage || state.forceCleanup) &&
        state.existingCoverage?.id
      ) {
        ids.push(state.existingCoverage.id);
      }
      return ids;
    }, []),
  );

  const subscriberResolutions = suppressRelatedPersonDeletesRetainedInBatch(
    await Promise.all(
      tierStates.map((state) =>
        resolveSubscriberRelatedPerson(
          medplum,
          clientId,
          state.tier,
          answers,
          state.existingCoverage,
          sentry,
          coverageIdsNoLongerReferencingSubscriber,
          state.willDeleteCoverage,
          state.forceCleanup,
        ),
      ),
    ),
  );

  const relatedPersonBuilds = tierStates.map((state, index) => {
    const existingRelatedPerson = subscriberResolutions[index]?.existingRelatedPerson;
    const fullUrl = existingRelatedPerson?.id
      ? joinReferenceString("RelatedPerson", existingRelatedPerson.id)
      : relatedPersonFullUrlByTier[state.name];

    return {
      entry: buildSubscriberRelatedPerson(
        state.tier,
        answers,
        profile,
        fullUrl,
        Boolean(state.organization),
        existingRelatedPerson?.id ? existingRelatedPerson : undefined,
      ),
      fullUrl,
      existingRelatedPerson,
    };
  });

  // Read only when a Coverage is created or its payor changes; the payor's verification may have
  // landed before this save. Verifications live in the practice's compartment, so without a
  // practice there is none to read.
  const hasNewPayor = tierStates.some(
    (state) =>
      state.organization &&
      (!state.existingCoverage || !isSamePayor(state.existingCoverage, state.organization)),
  );
  const verificationResponses =
    hasNewPayor && practiceReference
      ? await fetchActiveVerificationResponses(medplum, clientId, practiceReference)
      : [];

  const payorVerificationAnswersByTier = tierStates.map(
    (state) =>
      state.organization &&
      findPayorVerificationAnswers(
        verificationResponses,
        state.name,
        state.organization,
        get(answers[state.tier.coverageFields.insuranceId]) as string | undefined,
      ),
  );

  const coverageEntries = tierStates.map((state, index) => {
    const relatedPersonBuild = relatedPersonBuilds[index]!;
    const subscriberReference = resolveSubscriberReference(
      state.tier,
      answers,
      profile,
      relatedPersonBuild.fullUrl,
      relatedPersonBuild.entry?.resource ?? relatedPersonBuild.existingRelatedPerson,
    );

    return buildCoverage(
      state.tier,
      answers,
      profile,
      subscriberReference,
      state.organization,
      state.existingCoverage,
      payorVerificationAnswersByTier[index],
    );
  });

  // Clear order on the tier's other active Coverages only when the tier's target is active. When
  // the target is cancelled, the other Coverages keep their order. An active Coverage without an
  // order can still be picked as the payer.
  const targetCoverageByOrder = new Map(
    tierStates.map((state) => [state.tier.order, state.existingCoverage] as const),
  );
  const displacedCoverages = existingCoverages.reduce<BundleEntry<Coverage>[]>(
    (entries, existingCoverage) => {
      const order = existingCoverage.order;
      if (existingCoverage.status !== CoverageStatus.Active) return entries;
      if (order !== 1 && order !== 2 && order !== 3) return entries;
      const targetCoverage = targetCoverageByOrder.get(order);
      if (existingCoverage.id === targetCoverage?.id) return entries;
      if (targetCoverage?.status !== CoverageStatus.Active) return entries;

      const updated = { ...existingCoverage };
      delete updated.order;
      entries.push({
        resource: updated,
        request: {
          method: "PUT",
          url: getReferenceString(existingCoverage)!,
        },
      });
      return entries;
    },
    [],
  );

  const relatedPersonDeleteEntries = uniqueRelatedPersonDeleteEntries(
    subscriberResolutions.map((resolution) => resolution.deleteEntry),
  );

  const entries: BundleEntry[] = [
    ...relatedPersonBuilds.map((build) => build.entry),
    ...coverageEntries,
    ...relatedPersonDeleteEntries,
    ...displacedCoverages,
  ].filter(Boolean) as BundleEntry[];

  let batchResponse: Bundle | undefined;
  if (entries.length > 0) {
    const response = await medplum.executeBatch({
      resourceType: "Bundle",
      type: "transaction",
      entry: entries,
    });

    response.entry?.forEach((entry) => {
      const entrySuccess = entry.response?.status[0] === "2";
      if (!entrySuccess) {
        logBundleEntryError(entry, "Billing Information", sentry);
      }
    });
    batchResponse = response;
  }

  await observationReconciliationPromise;

  const unverifiedNewCoverageTiers = tierStates.reduce<InsuranceTierConfig[]>(
    (tiers, state, index) => {
      const isCreated = coverageEntries[index]?.request?.method === "POST";
      if (isCreated && !payorVerificationAnswersByTier[index]) tiers.push(state.tier);
      return tiers;
    },
    [],
  );
  if (batchResponse && practiceReference && unverifiedNewCoverageTiers.length > 0) {
    const appliedCoverageIds = await applyLateVerificationsToCreatedCoverages(
      medplum,
      clientId,
      practiceReference,
      unverifiedNewCoverageTiers,
      batchResponse,
    );
    if (appliedCoverageIds.length > 0) {
      console.warn("[billing-information] Applied verifications that landed during this save", {
        coverageIds: appliedCoverageIds,
      });
    }
  }

  return true;
}
